import React from "react";
import {
  Card,
  CardContent,
  Box,
  Typography,
  Button,
  Chip,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";

export type Product = {
  id: number;
  title: string;
  price?: number;
  image: string;
  fireRating: string;
  category: string;
  slug: string;
  images?: string[];
  model3d?: {
    url: string;
    type: "obj" | "stl" | "fbx";
  };
  specifications?: {
    dimensions: string;
    weight: string;
    material: string;
    thickness: string;
    glassPanelSize?: string;
    lockType: string;
    hingeType: string;
    colorOptions: string[];
    certificates: string[];
    testStandards: string[];
    fireResistance: string;
    soundInsulation?: string;
    thermalInsulation?: string;
  };
  description?: string;
  features?: string[];
  showPrice?: boolean;
};

type Props = {
  product: Product;
  viewMode?: string;
};

const ProductCard: React.FC<Props> = ({ product }) => {
  const { t, i18n } = useTranslation();
  return (
    <Card
      sx={{
        height: "100%",
        position: "relative",
        overflow: "hidden",
        transition: "all .3s ease",
        "&:hover": {
          transform: "translateY(-6px)",
          boxShadow: "0 12px 40px rgba(0,0,0,.12)",
        },
      }}
    >
      {/* Portrait door-style image area */}
      <Box
        sx={{
          position: "relative",
          p: 2,
          background: "linear-gradient(180deg,#fff 0%,#f7f7f8 100%)",
          borderBottom: "1px solid #eee",
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: "100%",
            aspectRatio: "4 / 7",
            borderRadius: 2,
            border: "1px solid #e8e8e8",
            backgroundColor: "#fcfcfc",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          {/* Inner frame to evoke door look */}
          <Box
            sx={{
              position: "absolute",
              inset: 8,
              border: "1px solid #e1e1e1",
              borderRadius: 1.5,
            }}
          />
          <Box
            component="img"
            src={product.image}
            alt={product.title}
            sx={{
              maxWidth: "90%",
              maxHeight: "90%",
              objectFit: "contain",
              filter: "drop-shadow(0 6px 12px rgba(0,0,0,0.08))",
            }}
          />
        </Box>

        {/* Badges */}
        <Box
          sx={{
            position: "absolute",
            top: 12,
            left: 12,
            display: "flex",
            gap: 1,
          }}
        >
          <Chip
            label={product.fireRating}
            color="primary"
            size="small"
            sx={{ fontWeight: 700 }}
          />
        </Box>
        {/* 3D badge moved to footer area */}
      </Box>
      <CardContent sx={{ p: 3 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            minHeight: 56,
          }}
          gutterBottom
        >
          {product.title}
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
          }}
        >
          {product.showPrice && product.price ? (
            <Typography color="primary" fontWeight={800} variant="h6">
              {i18n.language.startsWith("tr")
                ? `₺${product.price.toLocaleString("tr-TR")}`
                : `${product.price.toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                  })}`}
            </Typography>
          ) : (
            <Chip
              label={t("productCard.contact")}
              variant="outlined"
              color="default"
            />
          )}

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {product.model3d && (
              <Chip
                label={t("productCard.view3d")}
                color="success"
                size="small"
              />
            )}
            <Button
              size="small"
              variant="outlined"
              component={RouterLink}
              to={`/products/${product.slug}`}
            >
              {t("productCard.details")}
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ProductCard;
